import { Debouncer } from "@tanstack/pacer/debouncer";

import { createLogger } from "@/logger";
import { recordEvent } from "@/services/telemetry";
import { type CollectionName, collectionDefs, type StoreSchema } from "@/store/collections";
import type { DirtyRows } from "@/store/EntityStore";
import type { DeleteOutcome } from "@/store/persistence/idbConnection";
import {
  deleteRowStore,
  IndexedDbRowStore,
  type PersistedTx,
  type PersistedUpsert,
} from "@/store/persistence/indexedDbAdapter";
import { store } from "@/store/store";
import { isEvictionClassStorageError } from "@/utils/localStorage";

const log = createLogger("storage");

/**
 * Bump when the persisted layout or StoreSchema row shapes change incompatibly. A mismatched
 * snapshot is discarded and the app cold-bootstraps — the replacement for the old migration-hash
 * wipe of the PGlite IndexedDB.
 *
 * 2 is the row-keyed layout: one object store per collection keyed by the collection's row key,
 * instead of one array per collection under a single store. `scope.ts`'s `baseVersion` moved to 6.0
 * with it, so `cleanupLegacyDatabases` sweeps the 5.0 databases and the changeover is a one-time
 * full bootstrap rather than a read of a layout this code no longer understands.
 *
 * This snapshot layer is kept over TanStack DB's own SQLite persistence
 * (@tanstack/browser-db-sqlite-persistence) deliberately: the vendor persister puts WASM
 * download/instantiation, an OPFS database open, and row-by-row hydration on the boot-critical
 * path, where this layer is a bulk IndexedDB read per collection — and the persister is still a 0.x
 * alpha that OPFS-less test environments can't exercise. It stays swappable behind this module's
 * boundary if that calculus changes.
 */
export const SNAPSHOT_FORMAT_VERSION = 2;

/** Writes settle for this long before a persist, so drain bursts serialize once. */
const PERSIST_DEBOUNCE_MS = 5_000;
/** A persist happens at least this often while writes keep arriving. */
const PERSIST_MAX_WAIT_MS = 30_000;

const COLLECTION_NAMES = Object.keys(collectionDefs) as CollectionName[];

export type SnapshotRestoreResult =
  | { status: "restored"; appMeta: unknown; savedAt: string; rowCount: number }
  | { status: "empty" }
  | { status: "incompatible" };

function snapshotDBName(scope: string): string {
  return `sbweb-store-${scope}`;
}

function rowStore(scope: string): IndexedDbRowStore {
  return new IndexedDbRowStore(snapshotDBName(scope), COLLECTION_NAMES, SNAPSHOT_FORMAT_VERSION);
}

/**
 * Deletes the scope's snapshot database, reporting whether the delete actually landed. `blocked`
 * and `error` mean the rows are still on disk — a caller wiping data before a reload must not
 * treat those as a completed wipe (WEB-2248).
 */
export async function deleteSnapshot(scope: string): Promise<DeleteOutcome> {
  return await deleteRowStore(snapshotDBName(scope));
}

/**
 * Loads a persisted snapshot into the store. Returns the app cursor metadata stored with it so
 * boot can delta from the snapshot's timeline (never from a cursor newer than the rows).
 */
export async function restoreSnapshot(scope: string): Promise<SnapshotRestoreResult> {
  try {
    const { meta, data } = await rowStore(scope).loadAll();
    if (!meta) return { status: "empty" };
    if (meta.formatVersion !== SNAPSHOT_FORMAT_VERSION) {
      await deleteSnapshot(scope);
      return { status: "incompatible" };
    }
    const loaded: { [K in CollectionName]?: StoreSchema[K][] } = {};
    let rowCount = 0;
    for (const [name, rows] of data) {
      loaded[name as CollectionName] = rows as never;
      rowCount += rows.length;
    }
    // `load` records no dirty rows: these came off disk, so the next flush must not rewrite them.
    store.load(loaded);
    return { status: "restored", appMeta: meta.appMeta, savedAt: meta.savedAt, rowCount };
  } catch (error) {
    log.error("[snapshot] restore failed", { error });
    noteRestoreFailure(error);
    recordEvent(
      "store.snapshot.restore_failed",
      { snapshot: { error: error instanceof Error ? error.message : String(error) } },
      "reliability",
      { isError: true },
    );
    return { status: "empty" };
  }
}

/**
 * Rows one IndexedDB transaction writes before the next takes over. Steady-state flushes are a
 * handful of rows and never reach this; the first flush after a cold bootstrap has the whole
 * workspace to write, and one transaction holding all of it keeps the main thread busy for the
 * entire run.
 */
export const PERSIST_ROWS_PER_CHUNK = 10_000;

/**
 * Turns the store's dirty rows into per-collection writes, split into chunks of at most
 * `PERSIST_ROWS_PER_CHUNK` rows. Only the changed keys are read out of the store, so the work is
 * proportional to the change rather than to the workspace.
 *
 * A collection's truncate rides on the first chunk that carries it — later chunks for the same
 * collection must not clear what the earlier ones just wrote.
 */
function writeChunks(dirty: DirtyRows<StoreSchema>): Map<string, PersistedTx<unknown>>[] {
  const chunks: Map<string, PersistedTx<unknown>>[] = [];
  let current = new Map<string, PersistedTx<unknown>>();
  let rows = 0;
  const flushChunk = () => {
    if (current.size > 0) chunks.push(current);
    current = new Map();
    rows = 0;
  };

  for (const [collection, change] of dirty) {
    const name = collection as string;
    let upserts: PersistedUpsert<unknown>[] = [];
    let deletes: string[] = [];
    let truncatePending = change.truncated;
    const emit = () => {
      if (upserts.length === 0 && deletes.length === 0 && !truncatePending) return;
      current.set(name, { truncate: truncatePending, upserts, deletes });
      truncatePending = false;
      upserts = [];
      deletes = [];
    };

    for (const key of change.deletes) {
      deletes.push(key);
      if (++rows >= PERSIST_ROWS_PER_CHUNK) {
        emit();
        flushChunk();
      }
    }
    for (const key of change.upserts) {
      const row = store.get(collection, key);
      // Gone from memory between the mark and the flush (a purge, or eviction) — persist the
      // absence rather than a stale row.
      if (row === undefined) deletes.push(key);
      else upserts.push({ key, row });
      if (++rows >= PERSIST_ROWS_PER_CHUNK) {
        emit();
        flushChunk();
      }
    }
    emit();
  }
  flushChunk();
  return chunks;
}

/**
 * Persists the given dirty rows plus the app cursor metadata. The cursor metadata lands only once
 * every row has, so a persist interrupted part-way never leaves cursors ahead of the rows on disk.
 *
 * Returns false when the write failed, so the caller can put the rows back. Row-scoped writes make
 * that matter more than it did: dropping a failed batch leaves those specific rows missing from
 * disk until they happen to change again, where whole-collection writes left the last complete
 * copy of the collection in place.
 */
export async function persistSnapshot(
  scope: string,
  getAppMeta: () => unknown,
  dirty: DirtyRows<StoreSchema>,
): Promise<boolean> {
  const started = performance.now();
  const chunks = writeChunks(dirty);
  if (chunks.length === 0) return true;
  try {
    const applied = await rowStore(scope).applyCommittedTxs(chunks, getAppMeta());
    recordEvent(
      "store.snapshot.persisted",
      {
        snapshot: {
          durationMs: Math.round(performance.now() - started),
          collections: applied.collections,
          rows: applied.rowsWritten,
          deletedRows: applied.rowsDeleted,
          chunks: applied.chunks,
        },
      },
      "diagnostic",
    );
    return true;
  } catch (error) {
    log.error("[snapshot] persist failed", { error });
    recordEvent(
      "store.snapshot.persist_failed",
      { snapshot: { error: error instanceof Error ? error.message : String(error) } },
      "reliability",
      { isError: true },
    );
    return false;
  }
}

/** Stop callback of the most recently started persistence loop, for {@link stopSnapshotPersistence}. */
let activeStopPersistence: (() => void) | null = null;

/** Set once this page load's restore hit an eviction-class failure; see {@link snapshotRestoreFailed}. */
let evictionClassRestoreFailure = false;

/**
 * Records an eviction-class *restore* failure. Only the classified errors count — a one-off
 * transaction abort is not the same as a backing store the browser will never open — and only the
 * read side: a failed persist says the next launch will cold-boot, not that this one did
 * (WEB-2251).
 */
export function noteRestoreFailure(error: unknown): void {
  if (isEvictionClassStorageError(error)) evictionClassRestoreFailure = true;
}

/**
 * Whether this page load failed to read its snapshot because the backing store is broken or
 * evicted rather than momentarily unavailable. A restore failure means the boot already fell back
 * to a full bootstrap, which is what makes this usable as a "don't fetch it all again" guard
 * (WEB-2251, WEB-2252). Deliberately says nothing about persist failures: a client that restored
 * warm and later cannot save still has a working in-memory store and a re-fetch would help it.
 */
export function snapshotRestoreFailed(): boolean {
  return evictionClassRestoreFailure;
}

/** Test-only reset of the per-page-load restore-failure flag. */
export function resetSnapshotFailureState(): void {
  evictionClassRestoreFailure = false;
}

/**
 * Stops the active snapshot persistence loop, if any. For callers about to wipe the snapshot and
 * reload: a `visibilitychange → hidden` flush firing between the wipe and the reload would rewrite
 * the just-deleted snapshot from the still-populated in-memory store (WEB-2248).
 */
export function stopSnapshotPersistence(): void {
  activeStopPersistence?.();
}

/**
 * Watches store writes and persists dirty rows after a quiet period (with a max wait), off the
 * write path. One writer per scope across tabs via the Web Locks API. Also flushes when the tab
 * hides, which is the last reliable moment before close.
 */
export function startSnapshotPersistence(scope: string, getAppMeta: () => unknown): () => void {
  let persisting = false;
  let stopped = false;
  /** When the writes now waiting to be persisted started arriving; the max wait runs from it. */
  let windowOpenedAt: number | null = null;

  /**
   * Pacer's debouncer has no max-wait option, so the deadline rides on its dynamic `wait`: every
   * write asks for the full quiet period, clamped to whatever is left of the max wait. A steady
   * stream of writes therefore shortens the wait until it lands on the deadline exactly, rather
   * than pushing the persist out for as long as the writes keep coming.
   */
  const scheduler = new Debouncer(
    () => {
      void runPersist();
    },
    {
      wait: () => {
        if (windowOpenedAt === null) return PERSIST_DEBOUNCE_MS;
        const untilDeadline = windowOpenedAt + PERSIST_MAX_WAIT_MS - Date.now();
        return Math.max(0, Math.min(PERSIST_DEBOUNCE_MS, untilDeadline));
      },
    },
  );

  /** Drops the pending persist and the max-wait deadline with it, so the next write opens a fresh one. */
  const cancelScheduled = () => {
    scheduler.cancel();
    windowOpenedAt = null;
  };

  const runPersist = async () => {
    cancelScheduled();
    if (stopped) return;
    // A persist already in flight took its own rows; anything marked since needs a fresh timer,
    // because the clear above just dropped the one that would have caught it.
    if (persisting) {
      schedule();
      return;
    }
    const dirty = store.consumeDirtyRows();
    if (dirty.size === 0) return;
    persisting = true;
    // Rows put back are only on disk once a later attempt writes them, and the timers were cleared
    // on entry — without re-arming, a tab that goes write-idle holds them until it hides, and one
    // killed before that loses them.
    let retry = false;
    try {
      const locks: LockManager | undefined = typeof navigator !== "undefined" ? navigator.locks : undefined;
      if (locks) {
        await locks.request(`sbweb-snapshot-${scope}`, { ifAvailable: true }, async (lock) => {
          if (!lock) {
            // Another tab is persisting this scope; put the rows back so the next quiet period
            // retries them.
            store.restoreDirtyRows(dirty);
            retry = true;
            return;
          }
          if (!(await persistSnapshot(scope, getAppMeta, dirty))) {
            store.restoreDirtyRows(dirty);
            retry = true;
          }
        });
      } else if (!(await persistSnapshot(scope, getAppMeta, dirty))) {
        store.restoreDirtyRows(dirty);
        retry = true;
      }
    } catch (error) {
      // `persistSnapshot` handles its own failures, so this is the lock request itself rejecting —
      // it does when the document is not fully active (teardown, bfcache). The rows are already
      // consumed at this point, and dropping them would lose writes this layer promised to keep.
      log.warn("[snapshot] persist attempt failed before writing", { error });
      store.restoreDirtyRows(dirty);
      retry = true;
    } finally {
      persisting = false;
      if (retry) schedule();
    }
  };

  const schedule = () => {
    if (stopped) return;
    windowOpenedAt ??= Date.now();
    scheduler.maybeExecute();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") scheduler.flush();
  };

  const unsubscribe = store.subscribeWrites(schedule);
  const hasDocument = typeof document !== "undefined";
  if (hasDocument) document.addEventListener("visibilitychange", onVisibilityChange);

  const stop = () => {
    stopped = true;
    cancelScheduled();
    unsubscribe();
    if (hasDocument) document.removeEventListener("visibilitychange", onVisibilityChange);
    if (activeStopPersistence === stop) activeStopPersistence = null;
  };
  activeStopPersistence = stop;
  return stop;
}
